# Route: /admin/supervisor-overrides
# Lists every tracking amendment / supervisor-override event in the tenant.
# Permission: SupervisorOverride:read
# Feature: F53b E.5 (UX polish — auditing supervisor overrides)

page-supervisor-overrides:
  type: Page
  archetype: list
  meta:
    title: $t.admin.supervisor_overrides.meta_title
    route: /admin/supervisor-overrides
    layout: shell
    auth: required
  data:
    overrides:
      model: SupervisorOverride[]
      refresh: on_mount
      data_states:
        loading:
          skeleton_count: 5
        empty:
          ref: supervisor-overrides-empty
        error: default
    filters:
      model: SupervisorOverrideFilterForm
      initial:
        from: ""
        to: ""
        supervisorUserId: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - supervisor-overrides-header
    - supervisor-overrides-filters
    - supervisor-overrides-table

# ── Header ───────────────────────────────────────────────────────────────────

supervisor-overrides-header:
  type: PageHeader
  content: $t.admin.supervisor_overrides.title

# ── Filter bar ───────────────────────────────────────────────────────────────

supervisor-overrides-filters:
  type: Section
  spatial:
    display: flex
    gap: $spacing.md
    padding: $spacing.md
    direction: row
    align: flex-end
    wrap: wrap
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - supervisor-overrides-filter-from
    - supervisor-overrides-filter-to
    - supervisor-overrides-filter-supervisor
    - supervisor-overrides-filter-apply
    - supervisor-overrides-filter-clear

supervisor-overrides-filter-from:
  type: Input
  input_type: datetime
  label: $t.admin.supervisor_overrides.filter_from
  bind: filters.from
  a11y:
    label: $t.admin.supervisor_overrides.filter_from_label
  spatial:
    flex: 1
    min_width: 10rem

supervisor-overrides-filter-to:
  type: Input
  input_type: datetime
  label: $t.admin.supervisor_overrides.filter_to
  bind: filters.to
  a11y:
    label: $t.admin.supervisor_overrides.filter_to_label
  spatial:
    flex: 1
    min_width: 10rem

supervisor-overrides-filter-supervisor:
  type: Input
  input_type: text
  label: $t.admin.supervisor_overrides.filter_supervisor
  placeholder: $t.admin.supervisor_overrides.filter_supervisor_placeholder
  bind: filters.supervisorUserId
  a11y:
    label: $t.admin.supervisor_overrides.filter_supervisor_label
  spatial:
    flex: 2
    min_width: 12rem

supervisor-overrides-filter-apply:
  type: Button
  content: $t.admin.supervisor_overrides.btn_apply
  variant: filled
  color: info
  a11y:
    label: $t.admin.supervisor_overrides.btn_apply_label
  action:
    type: custom
    data_source: overrides
    params: filters
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
    align_self: flex-end

supervisor-overrides-filter-clear:
  type: Button
  variant: ghost
  content: $t.common.filter_clear
  a11y:
    label: $t.common.filter_clear_label
  action:
    type: custom
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md
    align_self: flex-end

# ── Results table ────────────────────────────────────────────────────────────

supervisor-overrides-table:
  type: DataTable
  bind:
    rows: overrides
  a11y:
    label: $t.admin.supervisor_overrides.table_label
  spatial:
    overflow: auto
  columns:
    - key: timestamp
      label: $t.admin.supervisor_overrides.col_timestamp
      format: compliance-datetime
      width: 2fr
    - key: supervisorUserId
      label: $t.admin.supervisor_overrides.col_supervisor
      width: 1fr
    - key: originalEntryId
      priority: low
      label: $t.admin.supervisor_overrides.col_original_entry
      width: 2fr
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.xs
    - key: amendmentReasonCode
      label: $t.admin.supervisor_overrides.col_reason_code
      width: 1fr
    - key: amendmentReasonText
      label: $t.admin.supervisor_overrides.col_reason_text
      width: 3fr
    - key: domainId
      priority: medium
      label: $t.admin.supervisor_overrides.col_scope
      width: 1fr
      tokens:
        font_size: $font.size.xs
        color: $color_scheme.light.color.on-surface-muted
  # Click → routes to the original entry's detail page where the amendment
  # chain is visible under "Revision history".
  row_action:
    type: navigate
    route: /tracking/records/:id
    params:
      id: $row.originalEntryId
  empty: supervisor-overrides-empty

supervisor-overrides-empty:
  type: EmptyState
  content: $t.admin.supervisor_overrides.empty
  body: $t.admin.supervisor_overrides.empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.lg
